import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
  alpha,
  Fade,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useUploadStore, type UploadTask } from '../../store/uploadStore';
import { useSnackbar } from 'notistack';

export const GlobalUploadBar = () => {
  const theme = useTheme();
  const uploads = useUploadStore((state) => state.uploads);
  const removeUpload = useUploadStore((state) => state.removeUpload);
  const cancelUpload = useUploadStore((state) => state.cancelUpload);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const [, setTick] = useState(0);
  const uploadStartTimesRef = useRef<Record<string, number>>({});
  const { enqueueSnackbar } = useSnackbar();

  // Record start time for each upload when it first appears as 'uploading'; prune removed uploads
  useEffect(() => {
    const ids = new Set(uploads.map((u) => u.id));
    const ref = uploadStartTimesRef.current;
    Object.keys(ref).forEach((id) => {
      if (!ids.has(id)) delete ref[id];
    });
    uploads.forEach((u) => {
      if (u.status === 'uploading' && !ref[u.id]) {
        ref[u.id] = Date.now();
      }
    });
  }, [uploads]);

  // Tick every second while there are active uploads so ETA updates
  useEffect(() => {
    const hasUploading = uploads.some((u) => u.status === 'uploading');
    if (!hasUploading) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [uploads]);

  // Warn user before reload / close tab / leave site when uploads are in progress (native browser dialog)
  const isListeningRef = useRef(false);
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'You have file(s) uploading. If you leave now, uploads may be cancelled.';
    };
    const checkAndToggleListener = () => {
      const inProgress = useUploadStore.getState().uploads.some((u) => u.status === 'uploading');
      if (inProgress && !isListeningRef.current) {
        window.addEventListener('beforeunload', handleBeforeUnload);
        isListeningRef.current = true;
      } else if (!inProgress && isListeningRef.current) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        isListeningRef.current = false;
      }
    };
    checkAndToggleListener();
    const unsubscribe = useUploadStore.subscribe(checkAndToggleListener);
    return () => {
      unsubscribe();
      if (isListeningRef.current) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        isListeningRef.current = false;
      }
    };
  }, []);

  // Don't render anything if there are no uploads
  if (uploads.length === 0) {
    return null;
  }

  // Calculate overall progress and categorize uploads
  const activeUploads = uploads.filter((u) => u.status === 'uploading');
  const failedUploads = uploads.filter((u) => u.status === 'error');
  const successUploads = uploads.filter((u) => u.status === 'success');
  const totalProgress = activeUploads.length > 0
    ? Math.round(
        activeUploads.reduce((sum, upload) => sum + upload.progress, 0) / activeUploads.length
      )
    : 100;

  const hasActiveUploads = activeUploads.length > 0;

  // Generate summary text based on upload states
  const getSummaryText = () => {
    if (hasActiveUploads) {
      return `Uploading ${activeUploads.length} file${activeUploads.length > 1 ? 's' : ''}...`;
    }
    if (failedUploads.length > 0 && successUploads.length > 0) {
      return `${successUploads.length} completed, ${failedUploads.length} failed`;
    }
    if (failedUploads.length > 0) {
      return `${failedUploads.length} upload${failedUploads.length > 1 ? 's' : ''} failed`;
    }
    return `${uploads.length} upload${uploads.length > 1 ? 's' : ''} completed`;
  };

  const handleCancelUpload = (upload: UploadTask) => {
    if (upload.status === 'uploading') {
      cancelUpload(upload.id);
      enqueueSnackbar('Upload cancelled successfully', {
        variant: 'info',
        autoHideDuration: 3000,
      });
    } else {
      removeUpload(upload.id);
      if (upload.status === 'error') {
        enqueueSnackbar('Upload dismissed', { variant: 'info', autoHideDuration: 2000 });
      }
    }
  };

  const handleDismissClick = () => {
    if (hasActiveUploads) {
      setShowDismissDialog(true);
    } else {
      // No active uploads, just clear all
      uploads.forEach((upload) => removeUpload(upload.id));
    }
  };

  const handleConfirmDismiss = () => {
    // Cancel all active uploads
    uploads.forEach((upload) => {
      if (upload.status === 'uploading') {
        cancelUpload(upload.id);
      }
    });
    // Clear all uploads
    uploads.forEach((upload) => removeUpload(upload.id));
    setShowDismissDialog(false);
    enqueueSnackbar('All uploads dismissed', { variant: 'info' });
  };

  const handleResumeUpload = async (upload: UploadTask) => {
    if (!upload.isChunked || !upload.onResume) {
      enqueueSnackbar('Cannot resume this upload', { variant: 'error' });
      return;
    }

    try {
      // Remove the failed upload entry first (resumeChunkedUpload will create a new one)
      removeUpload(upload.id);
      // Resume the upload using stored handler (no toast here to avoid contradicting "Upload cancelled" if user then cancels the resumed upload)
      await upload.onResume();
    } catch (error) {
      enqueueSnackbar('Failed to resume upload', { variant: 'error' });
    }
  };

  const getStatusIcon = (upload: UploadTask) => {
    switch (upload.status) {
      case 'success':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      case 'cancelled':
        return <CloseIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
      case 'uploading':
        return <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getStatusText = (upload: UploadTask) => {
    switch (upload.status) {
      case 'success':
        return 'Completed';
      case 'error':
        // Show helpful message for resumable chunked uploads
        if (upload.isChunked && upload.onResume) {
          return 'Upload interrupted. Click Resume to continue.';
        }
        return upload.error || 'Failed';
      case 'cancelled':
        return upload.statusText || 'Cancelled';
      case 'uploading':
        return upload.statusText ? `${upload.statusText} (${upload.progress}%)` : `${upload.progress}%`;
      default:
        return upload.statusText || 'Pending';
    }
  };

  const getUploadEta = (upload: UploadTask): string => {
    if (upload.status !== 'uploading') return '';
    const startedAt = uploadStartTimesRef.current[upload.id];
    if (!startedAt) return 'Calculating time to upload';
    const elapsedSec = (Date.now() - startedAt) / 1000;
    if (elapsedSec < 2 || upload.progress <= 0) return 'Calculating time to upload';
    const ratePerSec = upload.progress / elapsedSec;
    const remaining = 100 - upload.progress;
    if (remaining <= 0) return 'Almost done';
    const etaSec = remaining / ratePerSec;
    if (etaSec < 60) return `~${Math.ceil(etaSec)} sec left`;
    if (etaSec < 3600) return `~${Math.ceil(etaSec / 60)} min left`;
    return `~${(etaSec / 3600).toFixed(1)} hr left`;
  };

  return (
    <>
      <Fade in>
        <Box
          sx={{
            position: 'fixed',
            top: 80,
            right: 24,
            zIndex: 1400,
            minWidth: 320,
            maxWidth: 420,
          }}
        >
        <Paper
          elevation={8}
          sx={{
            background: alpha(theme.palette.background.paper, 0.98),
            backdropFilter: 'blur(8px)',
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          {/* Summary Pill */}
          <Box
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              p: 2,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              '&:hover': {
                background: alpha(theme.palette.primary.main, 0.05),
              },
              transition: 'background 0.2s',
            }}
          >
            <CloudUploadIcon sx={{ color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {getSummaryText()}
              </Typography>
              {hasActiveUploads && (
                <LinearProgress
                  variant="determinate"
                  value={totalProgress}
                  sx={{
                    mt: 1,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }}
                />
              )}
            </Box>
            <IconButton size="small">
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDismissClick();
              }}
              title="Dismiss all"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Expanded Upload List */}
          <Collapse in={isExpanded}>
            <Box
              sx={{
                maxHeight: 400,
                overflowY: 'auto',
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {uploads.map((upload) => (
                <Box
                  key={upload.id}
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{ pt: 0.5 }}>{getStatusIcon(upload)}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                        sx={{ mb: 0.5 }}
                      >
                        {upload.fileName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          upload.status === 'error'
                            ? 'error.main'
                            : upload.status === 'success'
                            ? 'success.main'
                            : upload.status === 'cancelled'
                            ? 'warning.main'
                            : 'text.secondary'
                        }
                      >
                        {upload.status === 'uploading'
                          ? upload.statusText ? `${upload.statusText} (${upload.progress}%) · ${getUploadEta(upload)}` : `${upload.progress}% · ${getUploadEta(upload)}`
                          : getStatusText(upload)}
                      </Typography>
                      {upload.status === 'uploading' && (
                        <LinearProgress
                          variant="determinate"
                          value={upload.progress}
                          sx={{
                            mt: 1,
                            height: 3,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        />
                      )}
                      {/* Resume/Cancel buttons for failed chunked uploads */}
                      {upload.status === 'error' && upload.isChunked && upload.onResume && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResumeUpload(upload);
                            }}
                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                          >
                            Resume
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeUpload(upload.id);
                              enqueueSnackbar('Upload Cancelled', {
                                variant: 'info',
                                autoHideDuration: 2000,
                              });
                            }}
                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelUpload(upload);
                      }}
                      sx={{ mt: -0.5 }}
                      title={upload.status === 'uploading' ? 'Cancel upload' : 'Dismiss'}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Paper>
      </Box>
    </Fade>

    {/* Confirmation Dialog for dismissing with active uploads */}
    <Dialog
      open={showDismissDialog}
      onClose={() => setShowDismissDialog(false)}
      aria-labelledby="dismiss-dialog-title"
    >
      <DialogTitle id="dismiss-dialog-title">Cancel Active Uploads?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You have {activeUploads.length} active upload{activeUploads.length > 1 ? 's' : ''} in progress.
          Dismissing will cancel all uploads. Are you sure you want to continue?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowDismissDialog(false)}>Keep Uploading</Button>
        <Button onClick={handleConfirmDismiss} color="error" variant="contained">
          Cancel All
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};


